import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Dialog from 'components/UI/Dialog';
import ComponentIcon from 'components/UI/ComponentIcon';
import Row from 'components/UI/Layout/Row';
import Col from 'components/UI/Layout/Col';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckSquare, faSquare } from '@fortawesome/free-solid-svg-icons';
import Button from 'components/UI/Button';
import ErrorMessage from 'components/UI/ErrorMessage';
import { connect } from 'react-redux';
import { storeRequestQuoteBundleComponent, addRequestQuoteBundle } from 'store/dispatchers/requestQuotes.dispatchers';
import _, { keys } from 'lodash';
import RequestQuoteBundleForm from './RequestQuoteBundleForm';
import ServiceComponentIcon from '../../UI/ServiceComponentIcon';

function RequestQuoteBundle({
    requestQuoteId, showBundleModal, setShowBundleModal, components, saveRequestQuoteBundleComponents, updateRequestQuote, editQuoteBundle,
})
{
    const [requestQuoteBundle, setRequestQuoteBundle] = useState(null);
    const [selectedComponents, setSelectedComponents] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const handleSelectAll = () =>
    {
        if (keys(selectedComponents).length === components.length)
        {
            setSelectedComponents({});
            return;
        }

        const result = components.reduce((fullSelection, component) => ({ ...fullSelection, [component.id]: true }), {});
        setSelectedComponents(result);
    };

    const handleComponentSelection = (componentId) =>
    {
        setSelectedComponents({ ...selectedComponents, [componentId]: !selectedComponents[componentId] });
    };

    const cancelForm = () =>
    {
        setShowBundleModal(false);
    };

    const handleForm = async () =>
    {
        const filteredComponents = _.keys(selectedComponents).map((selectedComponent) => selectedComponents[selectedComponent] ? selectedComponent : null).filter((n) => n);
        if (filteredComponents.length === 0)
        {
            setShowBundleModal(false);
            return;
        }

        setIsLoading(true);
        setErrorMessage('');

        saveRequestQuoteBundleComponents({ filteredComponents, requestQuoteBundleId: requestQuoteBundle.id, requestQuoteId }).then(() =>
        {
            setShowBundleModal(false);
        }).catch((e) =>
        {
            setErrorMessage(e.message ?? e);
        }).finally(() =>
        {
            setIsLoading(false);
        });
    };

    useEffect(() =>
    {
        if (requestQuoteBundle && !editQuoteBundle)
        {
            updateRequestQuote(requestQuoteId, requestQuoteBundle);
        }

        setSelectedComponents(editQuoteBundle?.requestQuoteBundleComponents.reduce((preSelectedComponents, requestQuoteBundleComponent) => ({ ...preSelectedComponents, [requestQuoteBundleComponent.requestQuoteComponent.id]: true }), {}) ?? []);
        setRequestQuoteBundle(editQuoteBundle);
    }, [showBundleModal]);

    return (
        <Dialog
            model={{ value: showBundleModal, setter: setShowBundleModal }}
            containerOverflow="tw-overflow-visible"
        >
            {requestQuoteBundle === null
            && (
                <RequestQuoteBundleForm
                    requestQuoteId={requestQuoteId}
                    onSuccess={(response) =>
                    {
                        setRequestQuoteBundle(response);
                    }}
                    onCancel={() =>
                    {
                        setShowBundleModal(false);
                    }}
                />
            )}

            {requestQuoteBundle !== null
                && (
                    <Col>
                        <Row className="tw-py-2">
                            <h2 className="tw-text-xl tw-font-bold dark:tw-text-neutral-300">Select Package Items</h2>
                        </Row>
                        <ErrorMessage errorMessage={errorMessage} />

                        <Button
                            onClick={() => handleSelectAll()}
                            type="custom"
                            customBg="tw-bg-secondary-lighter tw-text-white"
                        >
                            {keys(selectedComponents).length === components.length ? 'Unselect All' : 'Select All'}
                        </Button>

                        {components.map((c) => (
                            <button
                                type="button"
                                key={c.id}
                                onClick={() => handleComponentSelection(c.id)}
                                className="tw-flex tw-flex-row tw-w-full tw-px-3 tw-py-3 tw-border-b tw-border-b-primary-default tw-text-left tw-text-primary-default"
                            >
                                <FontAwesomeIcon icon={selectedComponents[c.id] ? faCheckSquare : faSquare} className="tw-text-primary-default tw-mr-2 tw-flex" />
                                {c.component_type === 'Service' ? <ServiceComponentIcon className="tw-mr-3" service={c.requestComponent.informative_type ?? c.requestComponent.type} /> : <ComponentIcon component={c.component_type} className="tw-mr-3" />}
                                {c.component_type} - {c.requestComponent.name}
                            </button>
                        ))}
                        <div className="tw-ml-8 tw-text-right">
                            <Button onClick={cancelForm} customBg="tw-bg-white tw-text-black" type="custom" disabled={isLoading}>Cancel</Button>
                            <Button onClick={handleForm} type="primary" margin="tw-ml-2" disabled={isLoading} loading={isLoading}>Save</Button>
                        </div>
                    </Col>
                )}

        </Dialog>
    );
}

RequestQuoteBundle.propTypes = {
    components: PropTypes.array,
    showBundleModal: PropTypes.bool,
    setShowBundleModal: PropTypes.func,
    saveRequestQuoteBundleComponents: PropTypes.func,
    updateRequestQuote: PropTypes.func,
    requestQuoteId: PropTypes.number.isRequired,
    editQuoteBundle: PropTypes.any,
};

const mapDispatchToProps = (dispatch) => ({
    saveRequestQuoteBundleComponents(data)
    {
        return dispatch(storeRequestQuoteBundleComponent(data));
    },
    updateRequestQuote(requestQuoteId, requestQuoteBundle)
    {
        return dispatch(addRequestQuoteBundle(requestQuoteId, requestQuoteBundle));
    },
});

export default connect(null, mapDispatchToProps)(RequestQuoteBundle);
